import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import { Form, Input, FormFeedback, Label } from "reactstrap"

import LoadingButton from "../../components/Common/LoadingButton"
import AuthLayout from "../../components/Common/AuthLayout"
import PasswordInput from "../../components/Common/PasswordInput"
import PasswordRequirementsHint from "../../components/OnboardingComponents/PasswordRequirementsHint"

import { Link, useSearchParams } from "react-router-dom"
import withRouter from "../../components/Common/withRouter"

import { useDispatch } from "react-redux"
import { toast, Bounce } from "react-toastify"
import { post, get } from "../../api/axios"
import { getApiErrorMessage, toastApiError } from "../../helpers/apiError"
import { isNetworkError } from "../../helpers/networkError"
import { fetchMandatoryProjectAcceptInvite } from "../../helpers/mandatoryProjectAccept"
import endpoints from "../../api/endpoints"
import { setLogin } from "../../store/slices/authSlice"
import { resetOnboarding } from "../../store/slices/onboardingSlice"
import * as Yup from "yup"
import { useFormik } from "formik"
import PreferredCitySelect from "../../components/Common/PreferredCitySelect"
import PlacesCityInput from "../../components/Common/PlacesCityInput"
import ExpiredInviteModal from "../../components/Modals/ExpiredInviteModal"
import {
  PASSWORD_COMPLEXITY,
  PASSWORD_REQUIREMENTS,
  getPasswordFieldError,
} from "../../helpers/passwordRules"
import {
  OTHER_CITY_OPTION,
  buildRegisterPayload,
  emptyOtherAddress,
  isOtherCitySelection,
  otherAddressWhenSelected,
} from "../../helpers/registerOtherAddress"

dayjs.extend(utc)
dayjs.extend(timezone)

const PRIVACY_POLICY_URL = "https://amok.earth/privacy-policy/"
const TERMS_URL = "https://amok.earth/terms-conditions/"

const INVITE_ALREADY_USED_MESSAGE =
  "This invitation link has already been used. Your account has already been created. Please sign in to continue."

const getErrorStatus = error =>
  Number(error?.response?.status || error?.status || error?.data?.code) || null

const isInviteAlreadyUsedError = error => {
  if (!error || isNetworkError(error)) return false

  const message = getApiErrorMessage(error, "").toLowerCase()
  if (!message) return false

  // Invite already consumed / account already exists — not network or generic failures.
  return (
    message.includes("invitation link has already been used") ||
    message.includes("invite has already been used") ||
    message.includes("invitation has already been used") ||
    message.includes("already been used") ||
    message.includes("already used") ||
    message.includes("account has already been created") ||
    (message.includes("already been created") &&
      (message.includes("invitation") || message.includes("account"))) ||
    (message.includes("email") &&
      message.includes("already") &&
      (message.includes("taken") ||
        message.includes("exist") ||
        message.includes("registered") ||
        message.includes("created")))
  )
}

const isInviteExpiredError = error => {
  if (!error || isNetworkError(error) || isInviteAlreadyUsedError(error)) {
    return false
  }

  const status = getErrorStatus(error)
  if (status === 410) return true

  const message = getApiErrorMessage(error, "").toLowerCase()
  if (!message) return false

  return (
    message.includes("invitation link has expired") ||
    message.includes("invite has expired") ||
    message.includes("invitation has expired") ||
    message.includes("invite link has expired") ||
    (message.includes("expired") &&
      (message.includes("invitation") || message.includes("invite")))
  )
}

const CreateAccount = props => {
  document.title = " Create Account | AMOK . EARTH"

  const dispatch = useDispatch()
  const navigate = props.router?.navigate
  const [searchParams] = useSearchParams()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [invite, setInvite] = useState(null)
  const [showExpiredInviteModal, setShowExpiredInviteModal] = useState(false)
  const fcmToken = localStorage.getItem("fcm")
  const code = searchParams.get("code")
  const email = searchParams.get("email")

  const redirectInviteAlreadyUsed = () => {
    toast.error(INVITE_ALREADY_USED_MESSAGE, {
      position: "bottom-center",
      theme: "colored",
      transition: Bounce,
    })
    navigate("/login", { replace: true })
  }

  const openExpiredInviteModal = () => {
    setShowExpiredInviteModal(true)
  }

  const handleRequestInviteAgain = () => {
    setShowExpiredInviteModal(false)
    navigate("/request-invite", {
      replace: true,
      state: email ? { email } : undefined,
    })
  }

  useEffect(() => {
    if (!code || !email) {
      navigate("/login", { replace: true })
    }
  }, [code, email, navigate])

  const prefilledValues = useMemo(
    () => ({
      invite_code: code,
      email: invite?.email ?? email,
      first_name: invite?.first_name ?? "",
      last_name: invite?.last_name ?? "",
      timezone: dayjs.tz.guess(),
      password: "",
      password_confirmation: "",
      fcm_token: fcmToken,
      device_id: fcmToken,
      is_adult: false,
      terms_accepted: false,
      primary_address: {
        city_id: invite?.city_id ?? "",
      },
      secondary_address: {
        city_id: "",
      },
      tertiary_address: {
        city_id: "",
      },
      primary_other_address: emptyOtherAddress(),
      secondary_other_address: emptyOtherAddress(),
      tertiary_other_address: emptyOtherAddress(),
    }),
    [code, email, fcmToken, invite],
  )

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: prefilledValues,
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email address")
        .required("Please enter your email"),
      first_name: Yup.string().required("Please enter your First Name"),
      last_name: Yup.string(),
      password: Yup.string()
        .required("Please enter your password")
        .min(8, "The password field must be at least 8 characters.")
        .matches(PASSWORD_COMPLEXITY, PASSWORD_REQUIREMENTS),
      password_confirmation: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Please confirm your password"),
      primary_address: Yup.object({
        city_id: Yup.string().required("Please select a Primary City"),
      }),
      primary_other_address: otherAddressWhenSelected(
        "primary_address",
        "Primary",
      ),
      secondary_other_address: otherAddressWhenSelected(
        "secondary_address",
        "Secondary",
      ),
      tertiary_other_address: otherAddressWhenSelected(
        "tertiary_address",
        "Tertiary",
      ),
      is_adult: Yup.boolean()
        .oneOf([true], "You must confirm you are 18 or older")
        .required("You must confirm you are 18 or older"),
      terms_accepted: Yup.boolean()
        .oneOf([true], "You must accept the Terms & Conditions and Privacy Policy")
        .required(
          "You must accept the Terms & Conditions and Privacy Policy",
        ),
    }),
    onSubmit: async values => {
      if (isSubmitting) return
      setIsSubmitting(true)
      try {
        const payload = buildRegisterPayload(values)
        const response = await post(endpoints.register, payload, {
          skipErrorToast: true,
        })

        if (response?.data?.success && response?.data?.result?.token) {
          const user = response?.data?.result?.user
          dispatch(resetOnboarding())
          dispatch(
            setLogin({
              currentUser: user,
              jid: response?.data?.result?.token,
              isAuth: true,
              loginType: "credentials",
            }),
          )
          toast.success(response?.data?.message, {
            position: "bottom-center",
            theme: "colored",
            transition: Bounce,
          })

          if (Number(user?.total_projects) === 1) {
            try {
              const invite = await fetchMandatoryProjectAcceptInvite(user)
              if (invite?.id) {
                navigate("/dashboard", {
                  replace: true,
                  state: { initialAcceptInvite: invite },
                })
              } else {
                navigate("/dashboard", { replace: true })
              }
            } catch {
              navigate("/dashboard", { replace: true })
            }
          } else {
            navigate("/onboarding/ongoing-roles", { replace: true })
          }
        } else if (response?.data) {
          if (isInviteAlreadyUsedError(response)) {
            redirectInviteAlreadyUsed()
          } else if (isInviteExpiredError(response)) {
            openExpiredInviteModal()
          } else {
            toastApiError(response)
          }
        }
      } catch (error) {
        if (isInviteAlreadyUsedError(error)) {
          redirectInviteAlreadyUsed()
        } else if (isInviteExpiredError(error)) {
          openExpiredInviteModal()
        } else {
          toastApiError(error)
        }
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  const getInvite = async inviteCode => {
    if (!inviteCode) return
    try {
      const response = await get(endpoints.getInvite(inviteCode), {
        skipErrorToast: true,
      })
      if (response?.data?.success) {
        const result = response?.data?.result
        const inviteCity = result?.city
        const cityLabel = [
          inviteCity?.city || inviteCity?.name,
          inviteCity?.state,
        ]
          .filter(Boolean)
          .join(", ")
        setInvite({
          email: result?.email,
          first_name: result?.first_name,
          last_name: result?.last_name,
          city_id: inviteCity?.id,
          city_label: cityLabel || "",
        })
        return
      }

      // `get()` returns null on network failure (toast already shown) or
      // the error response when skipErrorToast is set.
      if (isInviteAlreadyUsedError(response)) {
        redirectInviteAlreadyUsed()
        return
      }

      if (isInviteExpiredError(response)) {
        openExpiredInviteModal()
        return
      }

      if (response != null) {
        toastApiError(response, "Failed to fetch invite details")
      }
      // Stay on Create Account for network / invalid / server errors.
    } catch (error) {
      if (isInviteAlreadyUsedError(error)) {
        redirectInviteAlreadyUsed()
        return
      }
      if (isInviteExpiredError(error)) {
        openExpiredInviteModal()
        return
      }
      if (!isNetworkError(error)) {
        toastApiError(error, "Failed to fetch invite details")
      }
    }
  }

  useEffect(() => {
    if (code) {
      getInvite(code)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once when invite code is present
  }, [code])

  const primaryCityId = validation.values.primary_address?.city_id || ""
  const secondaryCityId = validation.values.secondary_address?.city_id || ""
  const tertiaryCityId = validation.values.tertiary_address?.city_id || ""

  const handleCityTierChange = (tier, nextValue) => {
    validation.setFieldValue(`${tier}_address.city_id`, nextValue || "")
    if (!isOtherCitySelection(nextValue)) {
      validation.setFieldValue(`${tier}_other_address`, emptyOtherAddress())
    }
  }

  const renderOtherAddressFields = (tier, label) => {
    const otherKey = `${tier}_other_address`
    const otherValues = validation.values[otherKey] || emptyOtherAddress()
    const otherTouched = validation.touched[otherKey] || {}
    const otherErrors = validation.errors[otherKey] || {}
    const otherError =
      (typeof otherErrors === "string" && otherErrors) ||
      otherErrors.city ||
      otherErrors.state ||
      otherErrors.country ||
      ""
    const showError = Boolean(
      (otherTouched.city || otherTouched.state || otherTouched.country) &&
        otherError,
    )

    return (
      <PlacesCityInput
        id={`create-account-${tier}-other-city`}
        label={`${label} location`}
        required
        value={otherValues}
        onChange={next => validation.setFieldValue(otherKey, next)}
        onBlur={() => {
          validation.setFieldTouched(`${otherKey}.city`, true)
          validation.setFieldTouched(`${otherKey}.state`, true)
          validation.setFieldTouched(`${otherKey}.country`, true)
        }}
        invalid={showError}
        error={
          showError
            ? otherError || `Please select a ${label.toLowerCase()} city`
            : ""
        }
        placeholder="Search city, state, country…"
      />
    )
  }

  const hasInvitePrimaryCity = Boolean(invite?.city_id)

  return (
    <AuthLayout
      size="lg"
      create
      title="Create Account"
      subtitle="Complete your account setup to get started"
    >
      <ExpiredInviteModal
        isOpen={showExpiredInviteModal}
        onRequestAgain={handleRequestInviteAgain}
      />
      <Form
        autoComplete="off"
        className="auth-page__form auth-page__form--create"
        onSubmit={e => {
          e.preventDefault()
          validation.handleSubmit()
          return false
        }}
      >
        <div className="auth-page__section">
          <p className="auth-page__section-title">Account Information</p>

          <div className="auth-page__field">
            <Label className="form-label">Email Address</Label>
            <div className="d-flex emailDisableContainer justify-content-between align-items-center">
              <div>{validation.values.email}</div>
              <div className="emailDisableTag">Locked</div>
            </div>
          </div>

          <div className="auth-page__row">
            <div className="auth-page__col auth-page__col--half">
              <div className="auth-page__field">
                <Label className="form-label isRequired">First Name</Label>
                <Input
                  name="first_name"
                  autoComplete="off"
                  className="form-control"
                  placeholder="Enter first name"
                  type="text"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.first_name || ""}
                  invalid={
                    !!(
                      validation.touched.first_name &&
                      validation.errors.first_name
                    )
                  }
                />
                {validation.touched.first_name &&
                validation.errors.first_name ? (
                  <FormFeedback type="invalid">
                    {validation.errors.first_name}
                  </FormFeedback>
                ) : null}
              </div>
            </div>

            <div className="auth-page__col auth-page__col--half">
              <div className="auth-page__field">
                <Label className="form-label isOptional">Last Name</Label>
                <Input
                  name="last_name"
                  autoComplete="off"
                  className="form-control"
                  placeholder="Enter last name"
                  type="text"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.last_name || ""}
                  invalid={
                    !!(
                      validation.touched.last_name &&
                      validation.errors.last_name
                    )
                  }
                />
                {validation.touched.last_name && validation.errors.last_name ? (
                  <FormFeedback type="invalid">
                    {validation.errors.last_name}
                  </FormFeedback>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <hr className="auth-page__divider-line" />

        <div className="auth-page__section">
          <p className="auth-page__section-title">City Scoping</p>

          <div className="auth-page__row">
            <div className="auth-page__col auth-page__col--third">
              <div className="auth-page__field">
                <PreferredCitySelect
                  variant="auth"
                  loadFromApi
                  label="Primary City"
                  required
                  placeholder="Select city"
                  value={primaryCityId}
                  valueLabel={
                    hasInvitePrimaryCity ? invite?.city_label || "" : ""
                  }
                  excludeIds={[secondaryCityId, tertiaryCityId].filter(
                    id => id && !isOtherCitySelection(id),
                  )}
                  extraOptions={[OTHER_CITY_OPTION]}
                  onChange={next => handleCityTierChange("primary", next)}
                  onBlur={() =>
                    validation.setFieldTouched("primary_address.city_id", true)
                  }
                  error={
                    validation.touched.primary_address?.city_id &&
                    validation.errors.primary_address?.city_id
                      ? validation.errors.primary_address.city_id
                      : ""
                  }
                  isDisabled={hasInvitePrimaryCity}
                />
                {isOtherCitySelection(primaryCityId)
                  ? renderOtherAddressFields("primary", "Primary")
                  : null}
              </div>
            </div>

            <div className="auth-page__col auth-page__col--third">
              <div className="auth-page__field">
                <PreferredCitySelect
                  variant="auth"
                  loadFromApi
                  label="Secondary City"
                  labelClassName="form-label isOptional"
                  placeholder="Select city"
                  value={secondaryCityId}
                  excludeIds={[primaryCityId, tertiaryCityId].filter(
                    id => id && !isOtherCitySelection(id),
                  )}
                  extraOptions={[OTHER_CITY_OPTION]}
                  onChange={next => handleCityTierChange("secondary", next)}
                  onBlur={() =>
                    validation.setFieldTouched(
                      "secondary_address.city_id",
                      true,
                    )
                  }
                  error={
                    validation.touched.secondary_address?.city_id &&
                    validation.errors.secondary_address?.city_id
                      ? validation.errors.secondary_address.city_id
                      : ""
                  }
                />
                {isOtherCitySelection(secondaryCityId)
                  ? renderOtherAddressFields("secondary", "Secondary")
                  : null}
              </div>
            </div>

            <div className="auth-page__col auth-page__col--third">
              <div className="auth-page__field">
                <PreferredCitySelect
                  variant="auth"
                  loadFromApi
                  label="Tertiary City"
                  labelClassName="form-label isOptional"
                  placeholder="Select city"
                  value={tertiaryCityId}
                  excludeIds={[primaryCityId, secondaryCityId].filter(
                    id => id && !isOtherCitySelection(id),
                  )}
                  extraOptions={[OTHER_CITY_OPTION]}
                  onChange={next => handleCityTierChange("tertiary", next)}
                  onBlur={() =>
                    validation.setFieldTouched("tertiary_address.city_id", true)
                  }
                  error={
                    validation.touched.tertiary_address?.city_id &&
                    validation.errors.tertiary_address?.city_id
                      ? validation.errors.tertiary_address.city_id
                      : ""
                  }
                />
                {isOtherCitySelection(tertiaryCityId)
                  ? renderOtherAddressFields("tertiary", "Tertiary")
                  : null}
              </div>
            </div>
          </div>
        </div>

        <hr className="auth-page__divider-line" />

        <div className="auth-page__section">
          <p className="auth-page__section-title">Create Password</p>

          <div className="auth-page__row">
            <div className="auth-page__col auth-page__col--half">
              <div className="auth-page__field">
                <Label className="form-label isRequired">Password</Label>
                <PasswordInput
                  name="password"
                  autoComplete="new-password"
                  value={validation.values.password || ""}
                  placeholder="Enter password"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    !!getPasswordFieldError(
                      validation.touched.password,
                      validation.errors.password,
                    )
                  }
                />
                <div className="mt-2">
                  <PasswordRequirementsHint
                    value={validation.values.password}
                  />
                </div>
                {getPasswordFieldError(
                  validation.touched.password,
                  validation.errors.password,
                ) ? (
                  <div className="invalid-feedback d-block">
                    {getPasswordFieldError(
                      validation.touched.password,
                      validation.errors.password,
                    )}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="auth-page__col auth-page__col--half">
              <div className="auth-page__field">
                <Label className="form-label isRequired">
                  Confirm Password
                </Label>
                <PasswordInput
                  name="password_confirmation"
                  autoComplete="new-password"
                  value={validation.values.password_confirmation || ""}
                  placeholder="Confirm password"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    !!(
                      validation.touched.password_confirmation &&
                      validation.errors.password_confirmation
                    )
                  }
                />
                {validation.touched.password_confirmation &&
                validation.errors.password_confirmation ? (
                  <div className="invalid-feedback d-block">
                    {validation.errors.password_confirmation}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="auth-page__field">
          <div className="form-check">
            <Input
              type="checkbox"
              className="form-check-input"
              id="create-account-age-check"
              name="is_adult"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              checked={validation.values.is_adult}
              invalid={
                !!(validation.touched.is_adult && validation.errors.is_adult)
              }
            />
            <label className="form-check-label" htmlFor="create-account-age-check">
              I confirm that I am 18 years of age or older
            </label>
          </div>
          {validation.touched.is_adult && validation.errors.is_adult ? (
            <div className="invalid-feedback d-block">
              {validation.errors.is_adult}
            </div>
          ) : null}
        </div>

        <div className="auth-page__field">
          <div className="form-check">
            <Input
              type="checkbox"
              className="form-check-input"
              id="create-account-terms-check"
              name="terms_accepted"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              checked={validation.values.terms_accepted}
              invalid={
                !!(
                  validation.touched.terms_accepted &&
                  validation.errors.terms_accepted
                )
              }
            />
            <label
              className="form-check-label"
              htmlFor="create-account-terms-check"
            >
              I accept the{" "}
              <a
                href={TERMS_URL}
                className="auth-page__legal-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms &amp; Conditions
              </a>{" "}
              and{" "}
              <a
                href={PRIVACY_POLICY_URL}
                className="auth-page__legal-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            </label>
          </div>
          {validation.touched.terms_accepted &&
          validation.errors.terms_accepted ? (
            <div className="invalid-feedback d-block">
              {validation.errors.terms_accepted}
            </div>
          ) : null}
        </div>

        <LoadingButton loading={isSubmitting}>Create Account</LoadingButton>

        <p className="auth-page__footer-text">
          Already have an account?{" "}
          <Link to="/login" className="auth-page__footer-link">
            Login
          </Link>
        </p>
      </Form>
    </AuthLayout>
  )
}

export default withRouter(CreateAccount)

CreateAccount.propTypes = {
  error: PropTypes.any,
  history: PropTypes.object,
  loginUser: PropTypes.func,
  socialLogin: PropTypes.func,
}
